import { fetchDedupe } from 'fetch-dedupe';
import * as _ from 'lodash';
import { stringify } from 'qs';

import { window } from '../../globals';

//  Iterate over the incoming object of headers and return actual Headers.
function getInitialHeaders(incomingHeaders?: Record<string, string>): Headers {
  const headers = new Headers();
  // We have legacy back end routes that rely on the X-Requested-With header
  // provided by jQuery. This will polyfill the headers just in case
  // https://expressjs.com/en/api.html#req.xhr
  if (!incomingHeaders?.['X-Requested-With']) {
    headers.append('X-Requested-With', 'XMLHttpRequest');
  }
  if (incomingHeaders) {
    Object.entries(incomingHeaders).forEach(([key, value]) => {
      headers.append(key, value);
    });
  }
  return headers;
}

//  Determines the URL to fetch, including query string params.
function getFetchURL(url: string, queryParams?: Record<string, unknown>, encodeQuery?: boolean): string {
  return queryParams
    ? `${url}?${stringify(queryParams, {
      encode: !!encodeQuery || false,
    })}`
    : url;
}

//  Interface for consumers of this module
export type QueryOptions<RequestBody = void> = {
  method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: RequestBody;
  query?: Record<string, unknown>;
  headers?: Record<string, string>;
  csrf?: boolean;
  dedupe?: boolean;
  json?: boolean;
  responseType?: string | ((response: Response) => string);
  encodeQuery?: boolean;
  cache?: string;
};

type CancellablePostOptions = Pick<QueryOptions, 'query' | 'headers' | 'responseType' | 'encodeQuery'> & {
  signal: AbortSignal;
};

//  Interface for use inside query, related to window.fetch options.
type FetchOptions = Omit<RequestInit, 'headers'> & {
  headers: Headers; // Mark headers as required.
};

/**
 * Perform a fetch request. Uses fetch-dedupe under the hood.
 *
 * @param {string} url - URL to fetch from.
 * @param {QueryOptions<APIRequestBody>} options - Options for the query fetch. Optional RequestBody type parameter
 * @returns {Promise<Response & { data: APIResponse }>}
 */
export async function query<APIRequestBody, APIResponse>(
  url: string,
  options: QueryOptions<APIRequestBody>,
): Promise<Response & { data?: APIResponse }> {
  //  The actual URL we fetch
  const fetchURL = getFetchURL(url, options.query, options.encodeQuery);

  //  Options to be passed to window.fetch
  const fetchOptions: FetchOptions = {
    ..._.omit(options, ['headers', 'body', 'dedupe', 'responseType']), // omit properties we're overriding, as well as fetchDedupe-specific props.
    credentials: 'same-origin',
    headers: getInitialHeaders(),
  };

  //  Attach csrf token.
  if (options.method !== 'GET') {
    fetchOptions.headers.append('CSRF-Token', window._csrf); // eslint-disable-line no-underscore-dangle
  }

  //  Handle sending JSON as stringified body.
  if (options.json) {
    fetchOptions.headers.append('Content-Type', 'application/json');
    fetchOptions.body = JSON.stringify(options.body);
  }

  const response = await fetchDedupe<APIResponse>(
    fetchURL,
    fetchOptions,
    {
      dedupe: !!options.dedupe,
      responseType: options.responseType,
    },
  );

  //  Fetch does not reject on 404s/other "error" responses, so we need to manually reject here.
  if (!response.ok) {
    return Promise.reject(response);
  }

  return response;
}

/**
 * Helper for making a GET request.
 *
 * @param {string} url - URL to fetch from.
 * @param {boolean} [dedupe] - If the request should be deduped.
 * @param {Record<string, string>} [queryParams] - Query string paramaters.
 * @param {Partial<QueryOptions>} [extraOptions] - Additional options to be passed to query.
 */
export function get<ResponseType>(
  url: string,
  dedupe = false,
  queryParams?: Record<string, unknown>,
  extraOptions: Partial<QueryOptions> = {},
) {
  // RequestBody set to void
  return query<void, ResponseType>(url, {
    dedupe,
    method: 'GET',
    query: queryParams,
    ...extraOptions,
  });
}

/**
 * Helper for making a POST request.
 *
 * @param {string} url - URL to fetch from.
 * @param {object} body - Request body.
 * @param {Partial<QueryOptions>} [extraOptions] - Additional options to be passed to query.
 */
export function post<RequestBody, ResponseType>(
  url: string,
  body: RequestBody,
  extraOptions: Partial<QueryOptions> = {},
) {
  return query<RequestBody, ResponseType>(url, {
    body,
    method: 'POST',
    json: true,
    ...extraOptions,
  });
}

/**
 * Make a POST request that can be cancelled with an AbortSignal.
 *
 * @example
 * const controller = new AbortController();
 *
 * postCancellable('/api/check', { name: 'lodash' }, {
 *   signal: controller.signal,
 *   responseType: 'json',
 * }).catch((error) => {
 *   if (error.name === 'AbortError') {
 *     // request was cancelled by the caller
 *   }
 * });
 *
 * // Cancel the in-flight request:
 * controller.abort();
 */
export async function postCancellable<RequestBody, ResponseType>(
  url: string,
  body: RequestBody,
  options: CancellablePostOptions,
): Promise<Response & { data?: ResponseType }> {
  const fetchURL = getFetchURL(url, options.query, options.encodeQuery);
  const headers = getInitialHeaders(options.headers);

  // Set CSRF + Content-Type manually rather than going through the `query` helper
  // because `QueryOptions` does not expose `signal` (AbortSignal). If `query` ever
  // gains AbortSignal support, this function should delegate to it.
  headers.append('CSRF-Token', window._csrf); // eslint-disable-line no-underscore-dangle
  headers.append('Content-Type', 'application/json');

  const response = await fetchDedupe<ResponseType>(
    fetchURL,
    {
      body: JSON.stringify(body),
      credentials: 'same-origin',
      headers,
      method: 'POST',
      signal: options.signal,
    },
    {
      dedupe: false,
      responseType: options.responseType,
    },
  );

  if (!response.ok) {
    return Promise.reject(response);
  }

  return response;
}

// TODO put does not support files - update to support files or add api.file
/**
 * Helper for making a PUT request.
 *
 * @param {string} url - URL to fetch from.
 * @param {object} body - Request body.
 * @param {Partial<QueryOptions>} [extraOptions] - Additional options to be passed to query.
 */
export function put<RequestBody, ResponseType>(
  url: string,
  body: RequestBody,
  extraOptions: Partial<QueryOptions> = {},
) {
  return query<RequestBody, ResponseType>(url, {
    body,
    method: 'PUT',
    json: true,
    ...extraOptions,
  });
}

/**
 * Helper for making a PATCH request.
 *
 * @param {string} url - URL to fetch from.
 * @param {object} body - Request body.
 * @param {Partial<QueryOptions>} [extraOptions] - Additional options to be passed to query.
 */
export function patch<RequestBody, ResponseType = void>(
  url: string,
  body: RequestBody,
  extraOptions: Partial<QueryOptions> = {},
) {
  return query<RequestBody, ResponseType>(url, {
    body,
    method: 'PATCH',
    json: true,
    ...extraOptions,
  });
}

/**
 * Helper for making a DELETE request.
 * Named `remove` because delete is a reserved word in JSland.
 *
 * @param {string} url - URL to fetch from.
 * @param {Partial<QueryOptions>} [extraOptions] - Additional options to be passed to query.
 */
export function remove<RequestBody, ResponseType>(
  url: string,
  extraOptions: Partial<QueryOptions<RequestBody>> = {},
) {
  return query<RequestBody, ResponseType>(url, {
    method: 'DELETE',
    ...extraOptions,
  });
}

/**
 * Helper for fetching file Blobs.
 * Returns a promise with the [contentType, Blob]
 *
 * @param {string} url - URL which returns some file contents.
 * @param {Record<string, unknown>} queryParams - Query paramaters.
 * @param {Partial<QueryOptions>} - Additional fetch options.
 * @example
 * async function fetchFileMatch(revision: string, filePath: string): string {
 *   const [contentType, ] = await API.blob(`/api/${revision}/file/${filePath}`);
 *
 *   //  You can do something with the contentType
 *   if (contentType === 'json') {
 *      // ...
 *   }
 *
 *   // Similar to fetch's `resonse.json`
 *   const fileContents = await blob.text();
 *   return fileContents;
 * }
 */
export async function blob(
  url: string,
  queryParams?: Record<string, unknown>,
  extraOptions?: Partial<QueryOptions>,
): Promise<[string | null, Blob]> {
  const fetchURL = getFetchURL(url, queryParams, extraOptions?.encodeQuery);

  /**
   * Note that we do not use fetch-dedupe here, instead using the native
   * fetch, as it is possible that the response is re-used with dedupe.
   *
   * This is problematic because `response.blob` (like `response.json`) can only be called once,
   * and so if the request is re-used, it is possible that this has been called already.
   * This throws an error if called a second time, which is why we instead just use `fetch`.
   */
  const response = await fetch(fetchURL, {
    method: 'GET',
    headers: getInitialHeaders(),
    credentials: 'same-origin',
  });

  if (!response.ok && response.status === 404) {
    throw new Error(`Error fetching file blob: Not Found`);
  }

  if (!response.ok) {
    throw new Error(`Error fetching file blob: ${response.statusText}`);
  }

  return [
    response.headers.get('content-type'),
    await response.blob(),
  ];
}

/**
 * Same as the blob API above, but also calls `blob.text()` on
 * the Blob (if it exists) to turn it into a UTF8 text string.
 *
 * @param {string} url - URL which returns some file contents.
 * @param {Record<string, unknown>} queryParams - Query paramaters.
 * @param {Partial<QueryOptions>} - Additional fetch options.
 */
export async function blobText(
  url: string,
  queryParams?: Record<string, unknown>,
  extraOptions?: Partial<QueryOptions>,
): Promise<[string | null, string]> {
  const [contentType, maybeBlob] = await blob(url, queryParams, extraOptions);

  //  If blob is falsey, just return whatever was returned.
  return !maybeBlob ? [contentType, maybeBlob] : [
    contentType,
    await maybeBlob.text(),
  ];
}

/**
 * Helper for uploading files using FormData.
 * Uses fetchDedupe directly to ensure multipart/form-data is handled properly.
 *
 * @param {string} url - URL to upload to.
 * @param {File} file - File to upload.
 * @param {string} [fieldName] - FormData field name for the file.
 * @param {Record<string, string>} [extraFields] - Additional FormData fields.
 * @param {Partial<QueryOptions>} [extraOptions] - Additional options.
 */
export async function fileUpload<ResponseType>(
  url: string,
  file: File,
  fieldName = 'file',
  extraFields: Record<string, string> = {},
  extraOptions: Partial<QueryOptions> = {},
): Promise<Response & { data?: ResponseType }> {
  const formData = new FormData();
  formData.append(fieldName, file);

  // Add any extra fields to the FormData
  if (extraFields) {
    Object.entries(extraFields).forEach(([key, value]) => {
      formData.append(key, value);
    });
  }

  // Build headers with CSRF token
  const headers = getInitialHeaders(extraOptions.headers);
  headers.append('CSRF-Token', window._csrf); // eslint-disable-line no-underscore-dangle
  // Don't set Content-Type - let browser set it with boundary

  const response = await fetchDedupe<ResponseType>(
    url,
    {
      method: 'POST',
      body: formData,
      headers,
      credentials: 'same-origin',
    },
    {
      dedupe: !!extraOptions.dedupe,
      responseType: extraOptions.responseType,
    },
  );

  if (!response.ok) {
    return Promise.reject(response);
  }

  return response;
}
